// 2. Third-party library imports
import {
    filter,
    find,
    get,
    isEmpty
} from 'lodash-es';
import { Action } from 'redux';
import { channel } from 'redux-saga';
import {
    all,
    call,
    fork,
    put,
    select,
    take,
    takeLatest,
} from 'redux-saga/effects';
import { I18n } from 'aws-amplify/utils';

// 3. Local imports - Constants
import {
    API_NAME,
    DEFAULT_LOCALE,
    DEFAULT_REGION_NAME,
    initialPrimaryColor,
    initialSecondaryColor,
} from '../../config/config';
import {
    CompanyIdAttribute,
    IsOrganisationViewAttribute,
    RoleIdAttribute,
} from '../../constants/authUserAttributes';

// 4. Local imports - Utility functions
import {
    refreshCognitoAttributes,
    updateLessVariables,
    getTranslatedText,
    getRegionallyGraphqlResponse,
} from '../../utils/commonFunctions';
import I18nWrapper from '../../utils/I18nWrapper';

// 5. Local imports - GraphQL
import { _queries as queries } from '../../graphql/queries.graphql';

// 6. Local imports - Redux actions
import client, {
    post,
    get as getAPI
} from 'src/api';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';
import {
    changeEndpointsAction,
    loginUserAction,
    getCurrentUser
} from '../auth/authSlice';
import {
    clearCreditsStateAllTableFiltersAction
} from '../credits/creditSlice';
import {
    clearCustomersStateAllTableFiltersAction
} from '../customers/customerSlice';
import {
    clearInvoicesStateAllTableFiltersAction
} from '../invoices/invoiceSlice';
import {
    clearPaymentsStateAllTableFiltersAction
} from '../payments/paymentSlice';
import {
    clearTaskHistoryStateAllTableFiltersAction,
    clearTasksStateAllTableFiltersAction
} from '../tasks/taskSlice';
import {
    clearTicketsStateAllTableFiltersAction
} from '../tickets/ticketSlice';
import {
    clearUsersStateAllTableFiltersAction
} from '../users/userSlice';
import {
    clearOrganisationActiveTabData
} from '../organisations/organisationSlice';
import {
    setLocaleAction
} from '../common/commonSlice';
import {
    getCompanyWorkflowOptionsRequestAction,
    getCompanyWorkflowOptionsSuccessAction,
    getSelectedUserCompany,
    getSystemConfigurationSuccessAction,
    getUserCompaniesRequestAction,
    getUserCompaniesSuccessAction,
    resetAllCompanyDataTableFiltersAction,
    saveUserCompanyRequestAction,
    saveUserCompanyResponseAction,
    selectUserCompanyRequestAction,
    selectUserCompanySuccessAction,
    saveCompanyUserSettingsRequestAction,
    saveCompanyUserSettingsResponseAction,
    uploadCompanyLogoRequestAction,
    getCustomFieldsChangesRequestAction,
    getCompanyCustomWordingResponseAction,
    getCompanyCustomWordingRequestAction,
    saveCompanyCustomWordingRequestAction,
    saveCompanyCustomWordingResponseAction,
    saveCustomSidebarItemsRequestAction,
    saveCustomSidebarItemsResponseAction,
    getQuestionAnswerConfigurationRequestAction,
    getQuestionAnswerConfigurationResponseAction,
    saveQuestionAnswerConfigurationRequestAction,
    saveQuestionAnswerConfigurationSuccessAction
} from './companySlice';

// 7. Local imports - Types
import {
    CompaniesActionTypes,
    CompanyUserRole,
    SidebarItem,
    WordingItem,
    WorkflowOption
} from './types';
import { DynamicObject } from '../../utils/commonInterfaces';
import { DETAILS_TAB } from 'src/config/tableAndPageConstants';

/**
 * Function responsible for getting the companies the user belongs to from API.
 */
function* handleGetUserCompaniesRequest() {
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_COMPANY_FOR_USER
            }
        );

        const systemConfigurationData = get(
            res.data,
            'GetCompanyForUser.SystemConfiguration'
        );

        yield put(getSystemConfigurationSuccessAction(systemConfigurationData));

        const data = get(res.data, 'GetCompanyForUser.CompanyUserRoles');
        const companiesData = filter(
            data,
            (companyUserRole: CompanyUserRole) => {
                return !isEmpty(companyUserRole.Company);
            }
        );

        yield put(getUserCompaniesSuccessAction(companiesData));

        const currentUser: DynamicObject = yield select(getCurrentUser);
        const isOrgView = get(currentUser, IsOrganisationViewAttribute) === '1';

        if (!isEmpty(companiesData) && !isOrgView) {
            const selectedCompanyId = get(currentUser, CompanyIdAttribute);

            if (!selectedCompanyId) {
                yield put(selectUserCompanyRequestAction(companiesData[0]));
            } else {
                const currentCompanyData = find(
                    companiesData,
                    (userCompanyData: CompanyUserRole) =>
                        selectedCompanyId === userCompanyData.Company.CompanyId
                );

                const selectedCompanyData = currentCompanyData || companiesData[0];

                yield put(selectUserCompanyRequestAction(selectedCompanyData));
            }
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getUserCompaniesSuccessAction([]));
    }
}

/**
 * Function responsible for triggering the user company selected
 * and also handles company switching to sync the company selected here in UI
 * and the one stored in cognito.
 */
const userChannel = channel();
const selectCompanyChannel = channel();
function* handleSelectUserCompanyRequest({ payload: sagaPayload }: any) {
    const { companyUserRoleObject, callback, modal } = sagaPayload;

    try {
        const companyRegion = get(companyUserRoleObject, 'Company.Region');
        let currentCompanyIdUsed: any;

        if (companyRegion) {
            const selectedUserCompany: DynamicObject = yield select(getSelectedUserCompany);

            if (
                (!selectedUserCompany && companyRegion !== DEFAULT_REGION_NAME) 
                    || companyRegion !== get(selectedUserCompany, 'Company.Region')
            ) {
                const currentUser: DynamicObject = yield select(getCurrentUser);
                currentCompanyIdUsed = get(currentUser, CompanyIdAttribute);

                if (currentCompanyIdUsed) {
                    yield put(changeEndpointsAction(companyUserRoleObject));
                }
            }

            const companyLocale = get(
                companyUserRoleObject,
                'Company.LanguagePackage.Language',
                DEFAULT_LOCALE
            );

            if (companyLocale) {
                const storedSelectedUserCompany: CompanyUserRole | undefined = yield select(getSelectedUserCompany);
                const languagePack: DynamicObject = get(
                    storedSelectedUserCompany,
                    'Company.LanguagePackage',
                    {}
                )

                const currentLocale = get(languagePack, 'Language');

                if (currentLocale !== companyLocale) {
                    yield put(setLocaleAction(companyLocale));
                }
            }
        }

        const { Company, Role, User } = companyUserRoleObject;
        const { CompanyId: companyId } = Company;
        const { RoleId: roleId } = Role;
        const currentUser: DynamicObject = yield select(getCurrentUser);
        const isOrgView = get(currentUser, IsOrganisationViewAttribute) === '1';
        const currentCompanyId = get(currentUser, CompanyIdAttribute);
        const currentRoleId = get(currentUser, RoleIdAttribute);

        if (
            isOrgView ||
            companyId !== currentCompanyId ||
            roleId !== currentRoleId
        ) {
            yield call(post, {
                apiName: API_NAME,
                path: '/company/switch',
                options: {
                    body: {
                        CompanyId: companyId,
                    }
                }
            });

            yield call(
                refreshCognitoAttributes,
                async (_err: any, session: any) => {
                    if (get(session, CompanyIdAttribute)) {
                        userChannel.put(
                            loginUserAction({
                                currentUser: session,
                                isAuth: true,
                            })
                        );

                        selectCompanyChannel.put(
                            selectUserCompanySuccessAction(
                                companyUserRoleObject
                            )
                        );

                        if (!currentCompanyIdUsed) {
                            userChannel.put(
                                changeEndpointsAction(companyUserRoleObject)
                            );
                        }
                    }
                }
            );

            yield all([
                put(resetAllCompanyDataTableFiltersAction()),
                put(clearOrganisationActiveTabData())
            ]);
        } else {
            selectCompanyChannel.put(
                selectUserCompanySuccessAction(companyUserRoleObject)
            );
        }

        if (callback) {
            callback();
        }
        // calls for queries if needed        
    } catch (err) {
        modal?.error({
            title: getTranslatedText("Error"),
            content: getTranslatedText("Failed to switch companies"),
            onOk: () => {
                window.location.reload();
            },
            okText: getTranslatedText("OK")
        });

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called after all the functions called when switching the company has finished.
 * Responsible for syncing the Theme for the company.
 */
// eslint-disable-next-line
function* handleSelectUserCompanySuccess({
    payload: companyUserRoleObject,
}: any) {
    try {
        const companyObject = get(companyUserRoleObject, 'Company');
        const primaryColorTheme = get(companyObject, 'PrimaryColorTheme') || initialPrimaryColor;
        const secondaryColorTheme = get(companyObject, 'SecondaryColorTheme') || initialSecondaryColor;

        updateLessVariables({
            '@custom-primary-color': primaryColorTheme,
            '@custom-secondary-color': secondaryColorTheme,
        });

        return false;
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for saving the company details - talking to the API.
 */
function* handleSaveUserCompanyRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/company/save',
            options: {
                body: payload,
            }
        });
        
        yield put(saveUserCompanyResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };
            
            callback(response);
        }
    } catch (err) {
        yield put(saveUserCompanyResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for saving the company logo.
 */
function* handleUploadCompanyLogoRequest({ payload: sagaPayload }: any) {
    const { fileToUpload, callback } = sagaPayload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/company/upload/logo',
            options: {
                body: fileToUpload.preview,
            }
        });

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
        //if (callback) {
        //    const returnData = get(err.response, 'data')
        //        ? err.response.data
        //        : { Messages: [err.message] };
        //    returnData.IsSuccess = false;
        //    callback(returnData);
        //}
        //if (err instanceof Error) {
        //    console.log('Error', err);
        //} else {
        //    console.error('An unknown error occured.');
        //}
    }
}

/**
 * Function responsible for getting the company workflow names.
 */
function* handleGetCompanyWorkflowOptionsRequest({ payload: sagaPayload }: any) {
    try {
        const { companyIds, isOrgView } = sagaPayload;
        let res: DynamicObject;

        if (!isOrgView) {
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_WORKFLOW_NAMES_FOR_COMPANY,
                    variables: {
                        IncludePaymentPlanWorkflows: true,
                        IncludePaymentPlanApprovalWorkflows: true,
                        IncludeNewInvoiceWorkflows: true
                    }
                }
            );
        } else {
            const apiUrl: string | undefined = yield select(
                getOrganisationActionUrl
            );

            // To call async functions, use redux-saga's `call()`.
            res = yield call(
                getRegionallyGraphqlResponse,
                apiUrl,
                queries.GET_WORKFLOW_NAMES_FOR_COMPANY, 
                { 
                    IncludePaymentPlanWorkflows: true,
                    IncludePaymentPlanApprovalWorkflows: true,
                    IncludeNewInvoiceWorkflows: true,
                    CompanyIds: companyIds || undefined
                }
            );
        }

        const workflowOptions: WorkflowOption[] = get(
            res,
            'data.GetWorkflowNamesForCompany'
        );
        
        yield put(getCompanyWorkflowOptionsSuccessAction(workflowOptions));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCompanyWorkflowOptionsSuccessAction([])); // Empty if error
    }
}

const setLanguageAndVocabularies = async (language: string) => {
    const languageJson = await import(`../../languages/${language}.json`);
    I18nWrapper.putVocabulariesForLanguage(language, languageJson.default[language]);
    I18nWrapper.setLanguage(language);
};

/**
 * Function responsible for selected company.
 */
function* handleLanguageSettings({ payload: selectedUserCompany }: any) {
    try {        
        const language: string = yield call(getLanguageCode, selectedUserCompany);

        localStorage.setItem('language', language);
        yield call(setLanguageAndVocabularies, language);
    } catch (error) {
        console.error("Error loading language:", error);
    }
}

function* handleGetCustomFieldChangeRequest({ payload: sagaPayload }: any) {
    const errorMessage = `Error fetching credit changes list. Please try again later.`;

    try {
        const { payload, callback } = sagaPayload;
        const { filters, pageSize, currentPage } = payload;

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CHANGE_LINES_FOR_COMPANY,
                variables: {
                    ...filters,
                    PageSize: pageSize,
                    Skip: currentPage * 20,
                }
            }
        );

        const { ChangeLines } = get(res.data, 'GetChangeLinesForCompany');

        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CREDIT_CHANGES_TIMELINE.pageSize
                        ),
                },
            };

           if (callback) callback(responsePayload);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
}

/**
 * Function called for saving the company details - talking to the API.
 */
function* handleSaveCompanyUserSettingsRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/company/user/save',
            options: {
                body: payload,
            }
        });
        
        yield put(saveCompanyUserSettingsResponseAction());
        if (callback) {
            const response = {
                IsSuccess: true,
            };
            
            callback(response);
        }
    } catch (err) {
        yield put(saveCompanyUserSettingsResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function getLanguageCode(
    selectedUserCompany: CompanyUserRole | undefined, 
    includeUserLanguage: boolean = true
) {
    let language: string = DEFAULT_LOCALE;

    if (includeUserLanguage && selectedUserCompany && selectedUserCompany.User.LanguageCode) {
        language = selectedUserCompany.User.LanguageCode;
    } else if (selectedUserCompany && selectedUserCompany.Company.LanguageCode) {
        language = selectedUserCompany.Company.LanguageCode;
    }

    return language;
}

const getDefaultWording = async (language: string): Promise<WordingItem> => {
    try {
        const languageJson = await import(`../../languages/${language}_StudentPortal.json`);
        return languageJson.default[`${language}_StudentPortal`] || {};
    } catch (err) {
        console.error('Error loading default wording:', err);
        return {};
    }
}

function* getCompanyCustomWording(language: string) {
    let wordingItems: WordingItem = {};
    try {
        // Get custom wording settings
        const { body} = yield call(
            getAPI,
            {
                apiName: API_NAME, 
                path: `/company/custom-wording?locale=${language}`,
                options: {}
            }
        );
        const res: DynamicObject = yield body.json();
        const customWordings: DynamicObject = get(res, 'CustomWording', {});
        wordingItems = get(customWordings, language, {}) as WordingItem;
        const defaultWordingItems: WordingItem = yield call(getDefaultWording, language);
        for (const [key, value] of Object.entries(defaultWordingItems)) {
            if (!(key in wordingItems)) {
                wordingItems[key] = value;
            }
        }

    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }
    }
    
    return wordingItems;
};

function* handleGetCustomWordingCompany() {
    try {
        const selectedUserCompany: CompanyUserRole | undefined = yield select(getSelectedUserCompany);
        const language: string = yield call(getLanguageCode, selectedUserCompany, false);
        const wordingItems: WordingItem = yield call(getCompanyCustomWording, language);
        yield put(getCompanyCustomWordingResponseAction(wordingItems));
    } catch (error) {
        console.error("Error loading language:", error);
    }
};

function* handleSaveCompanyCustomWordingRequest({ payload: sagaPayload }: any) {
    const { customWording, callback } = sagaPayload;
    try {
        yield call(post, {
            apiName: API_NAME, 
            path: '/company/custom-wording', 
            options: {
                body: customWording,
            }
        });
        
        yield put(saveCompanyCustomWordingResponseAction());
        if (callback) {
            const response = {
                IsSuccess: true,
            };
            
            callback(response);
        }
        // calls for queries if needed
    } catch (err) {
        yield put(saveCompanyCustomWordingResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.data) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
};

function* handleSaveCustomSidebarItemsRequest({ payload: sagaPayload }: any) {
    const { sidebarItems, callback } = sagaPayload; 
    try {
        const selectedUserCompany: CompanyUserRole | undefined = yield select(getSelectedUserCompany);
        const customSidebarItems = get(selectedUserCompany, 'Company.StudentPortalSidebarItems', []);

        const finalSidebarItems: SidebarItem[] = [];
        sidebarItems.forEach((menuItem: SidebarItem) => {
            const existingItem = customSidebarItems.find((item: SidebarItem) => item.Route === menuItem.Route);
            if (existingItem) {
                finalSidebarItems.push(existingItem);
            } else {
                finalSidebarItems.push(menuItem);
            }
        });

        yield put(saveCustomSidebarItemsResponseAction(finalSidebarItems));
        
        if (callback) {
            const response = {
                IsSuccess: true,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.data) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
};

function* handleGetQuestionAnswerConfigurationRequest({payload: sagaPayload}:any) {
    const { callback } = sagaPayload;
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_QUESTION_ANSWER_CONFIGURATION_FOR_COMPANY,
            }
        );

        const helpQAData = get(
            res.data,
            'GetQuestionAnswerConfigurationForCompany.QuestionAnswerItems',
            []
        );

        yield put(getQuestionAnswerConfigurationResponseAction(helpQAData));

        res.IsSuccess = true;
        if (callback) callback(res);
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.data) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleSaveQuestionAnswerConfigurationRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    
    try {
        yield call(
            post,
            {
                apiName: API_NAME,
                path: '/company/save/questionanswer',
                options: {
                    body: payload
                }
            }
        );

        yield put(saveQuestionAnswerConfigurationSuccessAction());
        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }       

    } catch (err) {
        if (callback) {
            const returnData = get(err.response, 'data')
                ? err.response.data
                : { Messages: [err.message] };
            returnData.IsSuccess = false;
            callback(returnData);
        }
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for resetting all company table data filters and sort values to initial state..
 */
function* handleResetAllCompanyDataTableFilters() {
    yield put(clearTasksStateAllTableFiltersAction());
    yield put(clearTicketsStateAllTableFiltersAction());
    yield put(clearTaskHistoryStateAllTableFiltersAction());
    yield put(clearCustomersStateAllTableFiltersAction());
    yield put(clearInvoicesStateAllTableFiltersAction());
    yield put(clearCreditsStateAllTableFiltersAction());
    yield put(clearPaymentsStateAllTableFiltersAction());
    yield put(clearUsersStateAllTableFiltersAction());
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetUserCompaniesRequest() {
    yield takeLatest(
        getUserCompaniesRequestAction.type,
        handleGetUserCompaniesRequest
    );
}

function* watchSelectUserCompanyRequest() {
    yield takeLatest(
        selectUserCompanyRequestAction.type,
        handleSelectUserCompanyRequest
    );
}

function* watchSelectUserCompanySuccess() {
    yield takeLatest(
        selectUserCompanySuccessAction.type,
        handleSelectUserCompanySuccess
    );
}

function* watchSaveUserCompanyRequest() {
    yield takeLatest(
        saveUserCompanyRequestAction.type,
        handleSaveUserCompanyRequest
    );
}

function* watchUserChannel() {
    while (true) {
        const action: Action<any> = yield take(userChannel);

        yield put(action);
    }
}

function* watchSelectCompanyChannel() {
    while (true) {
        const action: Action<any> = yield take(selectCompanyChannel);
        yield call(handleLanguageSettings, action);
        yield put(action);
    }
}

function* watchUploadCompanyLogoRequest() {
    yield takeLatest(
        uploadCompanyLogoRequestAction.type,
        handleUploadCompanyLogoRequest
    );
}

function* watchResetAllCompanyDataTableFilters() {
    yield takeLatest(
        resetAllCompanyDataTableFiltersAction.type,
        handleResetAllCompanyDataTableFilters
    );
}

function* watchGetCompanyWorkflowOptionsRequest() {
    yield takeLatest(
        getCompanyWorkflowOptionsRequestAction.type,
        handleGetCompanyWorkflowOptionsRequest
    );
}

function* watchGetCompanyCustomWording() {
    yield takeLatest(
        getCompanyCustomWordingRequestAction.type,
        handleGetCustomWordingCompany
    );
}

function* watchSaveCompanyCustomWordingRequest() {
    yield takeLatest(
        saveCompanyCustomWordingRequestAction.type,
        handleSaveCompanyCustomWordingRequest
    );
}

function* watchSaveCompanyUserSettingsRequest() {
    yield takeLatest(
        saveCompanyUserSettingsRequestAction.type,
        handleSaveCompanyUserSettingsRequest
    );
}

function* watchGetCustomFieldsChangeRequest() { 
    yield takeLatest(
        getCustomFieldsChangesRequestAction.type,
        handleGetCustomFieldChangeRequest
    )
}

function *watchSaveCustomSidebarItemsRequest() {
    yield takeLatest(
        saveCustomSidebarItemsRequestAction.type,
        handleSaveCustomSidebarItemsRequest
    );
}

function* watchGetQuestionAnswerConfigurationRequest() {
    yield takeLatest(
        getQuestionAnswerConfigurationRequestAction.type,
        handleGetQuestionAnswerConfigurationRequest
    );
}

function* watchSaveQuestionAnswerConfigurationRequest() {
    yield takeLatest(
        saveQuestionAnswerConfigurationRequestAction.type,
        handleSaveQuestionAnswerConfigurationRequest
    );
}

function* handleGetConveraCredentialsForCompanyRequest({ payload: { callback } }: any) {
    try {
        const res: DynamicObject = yield call(
            [client, 'graphql'],
            { query: queries.GET_CONVERA_CREDENTIALS_FOR_COMPANY }
        );

        // ensure callback gets the keys
        if (callback) {
            callback({
                IsSuccess: true,
                ...res.data.GetConveraCredentialsForCompany,
            });
        }
    } catch (err) {
        console.error('Error fetching Convera credentials', err);
        if (callback) {
            callback({ IsSuccess: false, Messages: [err instanceof Error ? err.message : 'Unknown error'] });
        }
    }
}

function* watchGetConveraCredentialsForCompanyRequest() {
    yield takeLatest(
        CompaniesActionTypes.GET_CONVERA_CREDENTIALS_FOR_COMPANY_REQUEST,
        handleGetConveraCredentialsForCompanyRequest
    );
}


// We can also use `fork()` here to split our saga into multiple watchers.
function* companiesSaga() {
    yield all([
        fork(watchGetUserCompaniesRequest),
        fork(watchSelectUserCompanyRequest),
        fork(watchSelectUserCompanySuccess),
        fork(watchUserChannel),
        fork(watchSelectCompanyChannel),
        fork(watchSaveUserCompanyRequest),
        fork(watchUploadCompanyLogoRequest),
        fork(watchResetAllCompanyDataTableFilters),
        fork(watchGetCompanyWorkflowOptionsRequest),
        fork(watchSaveCompanyUserSettingsRequest),
        fork(watchGetCustomFieldsChangeRequest),
        fork(watchGetCompanyCustomWording),
        fork(watchSaveCompanyCustomWordingRequest),
        fork(watchSaveCustomSidebarItemsRequest),
        fork(watchGetQuestionAnswerConfigurationRequest),
        fork(watchSaveQuestionAnswerConfigurationRequest),
        fork(watchGetConveraCredentialsForCompanyRequest),

    ]);
}

export default companiesSaga;